import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {PartCategoryEnum} from '../../../core/enums/part-category.enum';
import {QuotePart} from '../../../core/models/quote-part.interface';
import {Quote} from '../../../core/models/quote.interface';
import {Part} from '../../../core/models/part.interface';
import {QuoteConfig} from '../../../core/models/quote-config.interface';
import {QuoteConfigShelf} from '../../../core/models/quote-config-shelf.interface';
import {ConfigTypeEnum} from '../../../core/enums/config-type.enum';
import {TitleCasePipe} from '@angular/common';
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

@Component({
	selector: 'app-part-list',
	templateUrl: './part-list.component.html',
	styleUrls: ['./part-list.component.scss']
})
export class PartListComponent implements OnInit, OnChanges {
	@Input() quote: Quote = {};
	@Input() isSeismic: boolean = false;
	@Input() partCatalog: Part[] = [];
	@Input() useReadOnlyView: boolean = false;
	@Output() updateQuoteParts: EventEmitter<QuotePart[]> = new EventEmitter<QuotePart[]>();

	quoteInitialized: boolean = false;
	manualPartsInitialized: boolean = false;

	categories: PartCategoryEnum[] = [];
	generatedParts: QuotePart[] = [];
	manuallyAddedParts: QuotePart[] = [];

	manualPartForm: FormGroup = this.fb.group({});

	// Arrays for calculations
	validWidthsForHangRods: number[] = [30, 36, 42, 48, 60];
	validDepthsForSplitting: number[] = [24, 30, 36];
	validHeightsForPosts: number[] = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15];

	constructor(private titleCasePipe: TitleCasePipe, private fb: FormBuilder) {}

	ngOnInit() {
		this.categories = Object.values(PartCategoryEnum);
		this.categories.forEach((category: PartCategoryEnum) => this.manualPartForm.addControl(category, this.fb.array([])));
		this.manualPartForm.valueChanges.subscribe(() => {
			this.updatePartsOnQuote();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['quote'] && this.quote) {
			this.separateQuoteParts();
			if (!this.useReadOnlyView) {
				if (!this.quoteInitialized) {
					// If this is the initial load, then we dont want to recalculate parts
					this.createManualPartForms();
					this.quoteInitialized = true;
				} else {
					this.generateParts();
				}
				if (!this.manualPartsInitialized) {
					if (this.manuallyAddedParts.length) {
						this.createManualPartForms();
					}
				}
			}
		}
	}

	get selectedParts(): QuotePart[] {
		return this.generatedParts.concat(this.manualPartForm.value);
	}

	getCatalogPartsByCategory(category: PartCategoryEnum): Part[] {
		return this.partCatalog.filter((part: Part) => part.category === category);
	}

	getManualPartsFormByCategory(category: PartCategoryEnum) {
		return this.manualPartForm.controls[category] as FormArray;
	}

	createManualPartForms(): void {
		this.manualPartsInitialized = !!this.manuallyAddedParts.length;
		this.categories.forEach((category: PartCategoryEnum) => {
			const partsOfCategory: QuotePart[] = this.manuallyAddedParts.filter((part: QuotePart) => part.category === category);
			partsOfCategory.forEach((part: QuotePart) => {
				const control = this.fb.group({
					id: [part.id],
					partId: [null],
					quoteId: [part.quoteId],
					partName: [part.partName],
					modelNumber: [part.modelNumber],
					qty: [part.qty, Validators.min(0)],
					price: [part.price, Validators.min(0)],
					weight: [part.weight, Validators.min(0)],
					type: [part.type],
					category: [part.category, Validators.required],
					comment: [part.comment],
					manuallyAdded: [part.manuallyAdded]
				});
				(this.manualPartForm.controls[category] as FormArray).push(control);
			});
		});
	}

	separateQuoteParts(): void {
		this.manuallyAddedParts = this.quote.parts?.filter((part: QuotePart) => part.manuallyAdded) ?? [];
		this.generatedParts = this.quote.parts?.filter((part: QuotePart) => !part.manuallyAdded) ?? [];
	}

	updatePartsOnQuote(): void {
		const manualPartMap: Map<string, QuotePart[]> = new Map<string, QuotePart[]>(Object.entries(this.manualPartForm.value));
		let manualParts: QuotePart[] = [];
		manualPartMap.forEach((value) => {
			const partsToAdd: QuotePart[] = value.filter((part) => part.qty);
			manualParts = manualParts.concat(partsToAdd);
		});
		this.updateQuoteParts.emit(this.generatedParts.concat(manualParts));
	}

	addManualPart(category: PartCategoryEnum): void {
		const manualPartForm = this.fb.group({
			id: [null],
			partId: [null],
			quoteId: [null],
			partName: [null],
			modelNumber: [null],
			qty: [null, Validators.min(0)],
			price: [null, Validators.min(0)],
			weight: [null, Validators.min(0)],
			type: [null],
			category: [category],
			comment: [null],
			manuallyAdded: [true]
		});
		this.getManualPartsFormByCategory(category).push(manualPartForm);
	}

	deleteManualPart(category: PartCategoryEnum, partIndex: number) {
		this.getManualPartsFormByCategory(category).removeAt(partIndex);
	}

	onPartSelect(partId: string, partControl: AbstractControl) {
		const catalogPart: Part | undefined = this.partLookUp(partId);
		if (catalogPart) {
			partControl.get('price')?.setValue(catalogPart.price);
			partControl.get('weight')?.setValue(catalogPart.weight);
			partControl.get('partName')?.setValue(catalogPart.description);
			partControl.get('category')?.setValue(catalogPart.category);
			partControl.get('type')?.setValue(catalogPart.type);
			partControl.get('qty')?.setValue(1);
		}
	}

	getConfigTypeName(value: ConfigTypeEnum): string {
		const key: string = Object.keys(ConfigTypeEnum)[Object.values(ConfigTypeEnum).indexOf(value)];
		if (!key) {
			return '';
		}

		return this.titleCasePipe.transform(key.replace('_', ' '));
	}

	/**
	 * Creates a generated part to add to the parts array of the Quote
	 * @param partNumber - The partNumber of the part, used for looking up the part in the catalog.
	 * @param category - The type of part we are adding, e.g. Rebar, Hang Rods, etc...
	 * @param qty - The quantity of parts to appear on the line item of the quote.
	 */
	addGeneratedPart(partNumber: string, category: PartCategoryEnum, qty: number): void {
		const part: Part | undefined = this.partLookUp(partNumber);
		if (part) {
			this.generatedParts.push({
				partName: part.description,
				modelNumber: part.partNumber,
				weight: part.weight,
				price: part.price,
				category,
				qty
			});
			return;
		}
		console.error(`Failed to add generated part, ${partNumber} not found.`);
	}

	/**
	 * Creates a generated shelf part to add to the parts array of the Quote.
	 * This is only for parts of the Shelf Category.
	 * @param shelfCount - The ShelfCount object containing the Part and Quantity values.
	 */
	addGeneratedShelf(shelfCount: ShelfCount): void {
		const part: Part | undefined = this.getShelfPart(shelfCount.part.width, shelfCount.part.depth, shelfCount.part.type);
		if (part) {
			this.generatedParts.push({
				partName: `${part.description} ${part.type ? '(' + this.getConfigTypeName(part.type) + ')' : ''}`,
				modelNumber: part.partNumber,
				weight: part.weight,
				price: part.price,
				type: shelfCount.part.type,
				category: PartCategoryEnum.SHELVES,
				qty: shelfCount.qty
			});
			return;
		}
		console.error(`Failed to add generated shelf, ${shelfCount.part.width}x${shelfCount.part.depth} ${shelfCount.part.type} not found.`);
	}

	/**
	 * Look up parts of the Shelf category in the Part Catalog by providing dimensions and type.
	 * @param width - The width of the shelf to look up.
	 * @param depth - The depth of the shelf to look up.
	 * @param type - The type of the shelf to look up, e.g. wg for Wire Grid.
	 */
	getShelfPart(width?: number, depth?: number, type?: ConfigTypeEnum): Part | undefined {
		return this.partCatalog.find((part: Part) => part.width === width && part.depth === depth && part.type === type);
	}

	/**
	 * Creates a unique string key for a particular shelf part.
	 * @param shelfPart - The part data of the shelf.
	 */
	getShelfPartKey(shelfPart: Part): string {
		return `${shelfPart.width}x${shelfPart.depth} ${shelfPart.type}`;
	}

	getPartsByCategory(category: PartCategoryEnum): QuotePart[] {
		let parts: QuotePart[] = this.getGeneratedPartsByCategory(category);
		if (this.useReadOnlyView) {
			parts = parts.concat(this.getManualPartsByCategory(category));
		}

		return parts;
	}

	/**
	 * Returns selected parts of a provided category.
	 * @param category - The category of parts to fetch.
	 */
	getGeneratedPartsByCategory(category: PartCategoryEnum): QuotePart[] {
		return this.generatedParts
			.filter((part: QuotePart) => part.category === category)
			.sort((a, b) => {
				if (a.partName && b.partName) {
					if (a.partName > b.partName) {
						return 1;
					} else if (a.partName < b.partName) {
						return -1;
					}
				}
				return 0;
			});
	}

	/**
	 * Returns selected parts of a provided category.
	 * @param category - The category of parts to fetch.
	 */
	getManualPartsByCategory(category: PartCategoryEnum): QuotePart[] {
		return this.manuallyAddedParts.filter((part: QuotePart) => part.category === category);
	}

	/**
	 * Calculates the total price of the selected parts.
	 */
	get totalPrice(): number {
		return this.generatedPrice + this.manualPrice;
	}

	get generatedPrice(): number {
		return this.generatedParts.reduce((accumulator: number, {price, qty}) => {
			return accumulator + (price ?? 0) * (qty ?? 0);
		}, 0);
	}

	get manualPrice(): number {
		return this.manuallyAddedParts.reduce((accumulator: number, {price, qty}) => {
			return accumulator + (price ?? 0) * (qty ?? 0);
		}, 0);
	}

	/**
	 * Look up parts in the part catalog by their part number.
	 * @param partNumber - The part number of the part to find.
	 */
	partLookUp(partNumber: string): Part | undefined {
		return this.partCatalog.find((part: Part) => part.partNumber === partNumber);
	}

	/**
	 * Build the generated parts list from the User Inputs and Selected Shelves
	 */
	generateParts(): void {
		// Reset the generated parts list
		this.generatedParts = [];

		// Part Counts
		const postCounts: Map<string, number> = new Map<string, number>();
		const hangRodCounts: Map<number, number> = new Map<number, number>();
		const rebarCounts: Map<number, number> = new Map<number, number>();
		const shelfCounts: Map<string, ShelfCount> = new Map<string, ShelfCount>();
		const beamCounts: Map<string, number> = new Map<string, number>();

		// Static Starters mean we need Left and Right Foot Plates
		const seismicSuffix: string = this.isSeismic ? '-S' : '-NS';
		if (this.quote.staticStarters) {
			this.addGeneratedPart(`FPL${seismicSuffix}`, PartCategoryEnum.PLATES, this.quote.staticStarters * 2);
			this.addGeneratedPart(`FPR${seismicSuffix}`, PartCategoryEnum.PLATES, this.quote.staticStarters * 2);
		}

		// Static Adders mean we need Double Foot Plates
		if (this.quote.staticAdders) {
			this.addGeneratedPart(`FPD${seismicSuffix}`, PartCategoryEnum.PLATES, this.quote.staticAdders * 2);
		}

		this.quote.configs?.forEach((config: QuoteConfig) => {
			const hangRodMultiplier: number = (config.hangShelf ?? 0) + (config.hangFront ?? 0) + (config.hangNoFront ?? 0);

			// Handle each shelf config
			config.quoteConfigShelves?.forEach((shelf: QuoteConfigShelf) => {
				const configShelfPart: Part | undefined = this.partCatalog.find((part: Part) => part.id === shelf.partId);

				if (this.validHeightsForPosts.includes(config.height ?? -1)) {
					// Accumulate L Posts
					if (shelf.starters) {
						const key: string = `UL${config.height}`;
						const value: number = (postCounts.get(key) ?? 0) + shelf.starters * 4;
						postCounts.set(key, value);
					}

					// Accumulate T Posts
					if (shelf.adders) {
						const key: string = `UT${config.height}`;
						const value: number = (postCounts.get(key) ?? 0) + shelf.adders * 2;
						postCounts.set(key, value);
					}
				}

				// Accumulate Hang Rods
				if (hangRodMultiplier && configShelfPart && configShelfPart.width && this.validWidthsForHangRods.includes(configShelfPart.width)) {
					const newHangRodsToAdd: number =
						((shelf.starters ?? 0) + (shelf.adders ?? 0)) * // Total shelves of these dimensions
						hangRodMultiplier * // Specified hang rod values added together
						((configShelfPart.depth ?? 0) >= 30 ? 2 : 1); // If shelf is deep, then we want to double the number
					const value: number = (hangRodCounts.get(configShelfPart.width) ?? 0) + newHangRodsToAdd;
					hangRodCounts.set(configShelfPart.width, value);
				}

				// Accumulate Rebar
				if (
					config.levelsToSplit &&
					configShelfPart &&
					configShelfPart.depth &&
					this.validDepthsForSplitting.includes(configShelfPart.depth)
				) {
					const rebarToAdd: number =
						((shelf.starters ?? 0) + (shelf.adders ?? 0)) * // Total shelves of these dimensions
						config.levelsToSplit; // How many of the shelf levels should be split
					const value: number = (rebarCounts.get(configShelfPart.depth) ?? 0) + rebarToAdd;
					rebarCounts.set(configShelfPart?.depth, value);
				}

				if (config.shelves) {
					// Accumulate Shelves
					const shelfPart: Part | undefined = this.partCatalog.find((part: Part) => part.id === shelf.partId);

					if (shelfPart && shelfPart.depth && shelfPart.type) {
						// Get the key to be used in the shelfCounts map
						const shelfPartKey: string = this.getShelfPartKey(shelfPart);
						// See if we have an existing ShelfCount, if not then initialize one
						const shelfCount: ShelfCount = this.getShelfCountFromShelfCounts(shelfCounts, shelfPartKey, shelfPart);

						const baseTotal: number = (shelf.starters ?? 0) + (shelf.adders ?? 0);
						const unitTotal: number = baseTotal * config.shelves;
						const hangRows: number = baseTotal * (config.hangShelf ?? 0);
						const extraParts: number = this.quote.needsExtraParts && unitTotal > 99 ? 2 : 0;
						let removeShelvesForSplitLevels: number = 0;

						// Do we need to split any shelves depth-wise?
						if (config.levelsToSplit && this.validDepthsForSplitting.includes(shelfPart.depth)) {
							removeShelvesForSplitLevels = baseTotal * config.levelsToSplit;
							const halfShelvesToAdd: number = removeShelvesForSplitLevels * 2;
							// Create or Update the half shelves that we need
							this.splitShelves(shelfCounts, shelfPart, shelfPart.type, halfShelvesToAdd);
						}

						// Update shelf counts
						shelfCount.qty += unitTotal + extraParts + hangRows - removeShelvesForSplitLevels;
						shelfCounts.set(shelfPartKey, shelfCount);
					}

					if (shelfPart) {
						this.accumulateDrlBeams(beamCounts, config, shelf, shelfPart);
						this.accumulateDrhBeams(beamCounts, config, shelf, shelfPart);
						this.accumulateDrcBeams(beamCounts, config, shelf, shelfPart);
					}
				}
			});
		});

		postCounts.forEach((value: number, key: string) => {
			if (value) {
				this.addGeneratedPart(`${key}`, PartCategoryEnum.POSTS, value);
			}
		});

		hangRodCounts.forEach((value: number, key: number) => {
			if (value) {
				if (this.quote.needsExtraParts) {
					if (value > 500) {
						value += 4;
					} else if (value > 100) {
						value += 2;
					}
				}
				this.addGeneratedPart(`HR${key}`, PartCategoryEnum.HANG_RODS, value);
			}
		});

		rebarCounts.forEach((value: number, key: number) => {
			if (value) {
				this.addGeneratedPart(`RB${key}`, PartCategoryEnum.REBAR, value);
			}
		});

		shelfCounts.forEach((value: ShelfCount) => {
			if (value.qty) {
				this.addGeneratedShelf(value);
			}
		});

		beamCounts.forEach((value: number, key: string) => {
			if (value) {
				if (this.quote.needsExtraParts && value > 100) {
					value += 2;
				}
				this.addGeneratedPart(key, PartCategoryEnum.BEAMS, value);
			}
		});

		this.updatePartsOnQuote();
	}

	/**
	 * Retrieve ShelfCount from the given ShelfCount map. If not found, creates a new ShelfCount object.
	 * @param shelfCounts - The ShelfCount map to look through.
	 * @param shelfPartKey - The key of the ShelfCount to look up.
	 * @param shelfPart - The shelf part to add to the new ShelfCount if created.
	 */
	getShelfCountFromShelfCounts(shelfCounts: Map<string, ShelfCount>, shelfPartKey: string, shelfPart: Part): ShelfCount {
		return shelfCounts.get(shelfPartKey) ?? {part: shelfPart, qty: 0};
	}

	/**
	 * Splits a shelf depth-wise and adds it to the ShelfCount map.
	 * @param shelfCounts - The ShelfCount map to add to.
	 * @param shelfPart - The Shelf Part to be split.
	 * @param type - The type of the shelf part to be split.
	 * @param qty - The quantity value to assign to the newly split shelf.
	 */
	splitShelves(shelfCounts: Map<string, ShelfCount>, shelfPart: Part, type: ConfigTypeEnum, qty: number): void {
		// Get the half shelf part
		const halfDepth: number = (shelfPart.depth ?? 0) / 2;
		const halfShelfPart: Part | undefined = this.getShelfPart(shelfPart.width ?? 0, halfDepth, type);
		if (!halfShelfPart) {
			console.error(`Could not find half shelf part: ${shelfPart.width}x${halfDepth} ${shelfPart.type}`);
			return;
		}

		// See if the half shelf exists, if not create it
		const halfShelfKey: string = this.getShelfPartKey(halfShelfPart);
		const halfShelfCount: ShelfCount = this.getShelfCountFromShelfCounts(shelfCounts, halfShelfKey, halfShelfPart);
		halfShelfCount.qty += qty;

		shelfCounts.set(halfShelfKey, halfShelfCount);
	}

	/**
	 * Calculates key and adds value to the BeamCounts Map
	 * @param beamCounts - The BeamCounts Map
	 * @param type - The type of beam to add
	 * @param dimensionValue - The width or depth of the shelf
	 * @param count - The number of beams to add
	 */
	addBeamsToCounts(beamCounts: Map<string, number>, type: string, dimensionValue: number, count: number): void {
		const key: string = `${type}${dimensionValue}`;
		const value: number = (beamCounts.get(key) ?? 0) + count;
		beamCounts.set(key, value);
	}

	/**
	 * Calculates DRL Beams based on configs
	 * @param beamCounts - The BeamCounts Map
	 * @param config - The Quote Config object
	 * @param shelf - The Shelf Config object
	 * @param shelfPart - The shelf part object
	 */
	accumulateDrlBeams(beamCounts: Map<string, number>, config: QuoteConfig, shelf: QuoteConfigShelf, shelfPart: Part): void {
		const drlCount: number = ((config.shelves ?? 0) - (config.thickLevels ?? 0)) * ((shelf.starters ?? 0) + (shelf.adders ?? 0)) * 2;
		if (drlCount > 0) {
			// Add DRLs for width
			if (shelfPart.width) {
				this.addBeamsToCounts(beamCounts, 'DRL', shelfPart.width, drlCount);
			}

			if (shelfPart.depth) {
				// Add DRLs for depth
				this.addBeamsToCounts(beamCounts, 'DRL', shelfPart.depth, drlCount);
			}
		}
	}

	/**
	 * Calculates DRH Beams based on configs
	 * @param beamCounts - The BeamCounts Map
	 * @param config - The Quote Config object
	 * @param shelf - The Shelf Config object
	 * @param shelfPart - The shelf part object
	 */
	accumulateDrhBeams(beamCounts: Map<string, number>, config: QuoteConfig, shelf: QuoteConfigShelf, shelfPart: Part): void {
		const drhCount: number = (config.thickLevels ?? 0) * ((shelf.starters ?? 0) + (shelf.adders ?? 0)) * 2;
		// Add DRHs for width
		if (shelfPart.width) {
			this.addBeamsToCounts(beamCounts, 'DRH', shelfPart.width, drhCount);
		}

		if (shelfPart.depth) {
			const forFtb: number = ((shelf.starters ?? 0) + (shelf.adders ?? 0)) * (config.addFtbDrh ?? 0) * 2;
			// Add DRHs for depth
			this.addBeamsToCounts(beamCounts, 'DRH', shelfPart.depth, drhCount + forFtb);
		}
	}

	/**
	 * Calculates DRC Beams based on configs
	 * @param beamCounts - The BeamCounts Map
	 * @param config - The Quote Config object
	 * @param shelf - The Shelf Config object
	 * @param shelfPart - The shelf part object
	 */
	accumulateDrcBeams(beamCounts: Map<string, number>, config: QuoteConfig, shelf: QuoteConfigShelf, shelfPart: Part): void {
		const shelfTotal: number = (shelf.starters ?? 0) + (shelf.adders ?? 0);
		const hangWithFrontCount: number = shelfTotal * (config.hangFront ?? 0) * 2;
		const hangWithShelf: number = shelfTotal * (config.hangShelf ?? 0) * 2;
		const hangRodNoFrontCount: number = shelfTotal * (config.hangNoFront ?? 0) * 2;
		// Add DRCs for width
		if (shelfPart.width) {
			this.addBeamsToCounts(
				beamCounts,
				'DRC',
				shelfPart.width,
				hangWithFrontCount + hangWithShelf + (shelfPart.width === 60 ? hangRodNoFrontCount : 0)
			);
		}

		if (shelfPart.depth) {
			// Add DRCs for depth
			this.addBeamsToCounts(beamCounts, 'DRC', shelfPart.depth, hangRodNoFrontCount + hangWithFrontCount + hangWithShelf);
		}
	}
}

interface ShelfCount {
	part: Part;
	qty: number;
}
